import React, { useState } from 'react';
import { Edit, Trash2 } from 'lucide-react';

/**
 * Unified Resource Card Component
 * Based on the clean Roles card design
 * Used for: Team, Assets, Roles
 */

const ResourceCard = ({
  // Data
  id,
  name,
  icon: Icon,
  iconColor = '#3B82F6',
  
  // Top-right badge (e.g. role name)
  topRightBadge, // { label, color }
  titleSuffix,   // JSX rendered immediately after the name (e.g. an AI sparkle)
  headerAction,  // JSX rendered far-right in the header row (e.g. an Active toggle)
  
  // Primary badge (shown next to name) — kept for back-compat
  primaryBadge,
  
  // Description/details text
  description,
  descriptionLines, // [{ icon: <ReactNode>, text: string }]
  
  // Details lines (for Team: email, phone; for Assets: capacity)
  details = [], // [{ icon, text }] or just array of strings
  
  // Expandable content (See More section)
  expandedContent,
  showSeeMore = false,
  isExpanded = false, // Controlled expansion state
  onToggleExpand, // Controlled expansion handler
  
  // Actions
  onEdit,
  onDelete,
  editLabel = 'Edit',
  deleteLabel = 'Delete',
  
  // Mass actions
  massActionMode = false,
  isSelected = false,
  onToggleSelect,
  
  // Left border colors (array for multi-color)
  leftBorderColors = [],
  
  // Additional elements
  additionalBadges, // JSX for extra badges (e.g., Pending)
  additionalClassNames = '', // For special cases like opacity, ring colors

  // Optional metric strip above the action buttons (e.g. { label, value })
  metric
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const expanded = onToggleExpand !== undefined ? isExpanded : internalExpanded;
  const toggleExpand = onToggleExpand || (() => setInternalExpanded(!internalExpanded));

  return (
    <div
      data-testid={`resource-card-root-${id}`}
      className={`card-soft relative overflow-hidden flex flex-col h-full transition-all duration-200 hover:shadow-[0_14px_36px_rgba(0,0,0,0.09)] hover:-translate-y-0.5 ${isSelected ? 'ring-2 ring-blue-500' : ''} ${additionalClassNames}`}
    >
      {/* Checkbox - top right */}
      {massActionMode && (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="absolute top-3.5 right-3.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer z-10"
        />
      )}

      <div className="p-5 pb-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          {/* Icon tile — soft tinted square, no harsh ring (matches dashboard stat tiles) */}
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${iconColor}14` }}
          >
            <Icon className="w-5 h-5" style={{ color: iconColor }} />
          </div>
          {/* Name — larger, lighter weight for the airy dashboard feel */}
          <div className="flex flex-1 min-w-0 items-center gap-1.5">
            <span className="text-base font-medium text-gray-900 tracking-tight truncate min-w-0">{name}</span>
            {titleSuffix}
          </div>
          {/* Role badge — top right */}
          {topRightBadge && (
            <span
              className="text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 max-w-[88px] truncate mr-5 sm:mr-0"
              style={{ backgroundColor: `${topRightBadge.color}14`, color: topRightBadge.color }}
            >
              {topRightBadge.label}
            </span>
          )}
          {/* Header action — far right (e.g. Active on/off toggle) */}
          {headerAction && <div className="flex-shrink-0">{headerAction}</div>}
        </div>

        {/* Details (used by assets etc.) */}
        {details.length > 0 && (
          <div className="mt-3 space-y-1">
            {details.slice(0, 3).map((detail, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-gray-500 truncate">
                {typeof detail === 'string' ? <span className="truncate">{detail}</span> : (
                  <>
                    {detail.icon && <span className="flex-shrink-0 text-gray-400">{detail.icon}</span>}
                    <span className="truncate">{detail.text}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {additionalBadges && <div className="mt-2">{additionalBadges}</div>}
      </div>

      <div className="flex flex-col flex-1">
        {/* Description — fixed 2-line height, supports icon+text lines or plain text */}
        {(description !== undefined || descriptionLines?.length > 0) && (
          <div className="px-5 pb-3 min-h-[2.5rem] overflow-hidden">
            {descriptionLines?.length > 0 ? (
              <div className="space-y-1">
                {descriptionLines.slice(0, 3).map((line, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-500 truncate">
                    {line.icon && <span className="flex-shrink-0 text-gray-400">{line.icon}</span>}
                    <span className="truncate">{line.text}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 line-clamp-2 leading-snug whitespace-pre-line">
                {description || ''}
              </p>
            )}
          </div>
        )}

        {showSeeMore && expandedContent && (
          <div className="px-5 pb-2">
            <button onClick={toggleExpand} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
              {expanded ? 'Less' : 'See More'}
            </button>
          </div>
        )}
        {expanded && expandedContent && (
          <div className="space-y-3 px-5 mb-3 pt-3 border-t border-gray-100">
            {expandedContent}
          </div>
        )}

        {/* Spacer — pushes Edit/Delete to bottom */}
        <div className="flex-1" />

        {/* Optional metric strip (e.g. member count, trailing revenue) — right-aligned */}
        {metric && (
          <div className="px-5 py-2.5 flex items-center justify-between" data-testid={`resource-card-metric-${id}`}>
            <span className="text-xs text-gray-400">{metric.label}</span>
            <span className="text-sm font-normal" style={{ color: metric.color || '#2563EB' }}>{metric.value}</span>
          </div>
        )}

        {/* Action Buttons — always flush at card bottom */}
        <div className="grid grid-cols-2 border-t border-gray-100 overflow-hidden">
          <button
            onClick={onEdit}
            data-testid={`resource-card-edit-${id}`}
            className="flex items-center justify-center gap-1.5 py-3 text-[13px] font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-50/70 active:bg-gray-100 transition-colors outline-none focus:outline-none rounded-bl-2xl"
          >
            <Edit className="w-3.5 h-3.5" />
            {editLabel}
          </button>
          {onDelete ? (
            <button
              onClick={onDelete}
              data-testid={`resource-card-delete-${id}`}
              className="flex items-center justify-center gap-1.5 py-3 text-[13px] font-medium text-red-400 hover:text-red-600 hover:bg-red-50/60 active:bg-red-50 transition-colors border-l border-gray-100 outline-none focus:outline-none rounded-br-2xl"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {deleteLabel}
            </button>
          ) : (
            <div className="rounded-br-2xl" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ResourceCard;
